import { rememberAskInputDraftDatabase } from '#pplx/frontend/apps/ask/ask-input/ask-input-drafts/askInputDraftDatabase.js';
import { ASK_INPUT_DRAFTS_DB_NAME } from '#pplx/frontend/apps/ask/ask-input/ask-input-drafts/types.js';
import { getAccountScopedStore } from '#pplx/frontend/browser-storage/accountScopedIDB.js';
import { validateUuid } from '#pplx/frontend/common/validateUuid.js';
import { clear, del, delMany, entries, get, set } from 'idb-keyval';
const ASK_INPUT_DRAFTS_STORE_NAME = 'drafts-v1';
const MAX_DRAFT_COUNT = 50;
const DRAFT_TTL_DAYS = 30;
const DRAFT_TTL_MS = DRAFT_TTL_DAYS * 24 * 60 * 60 * 1_000;
function isNonArrayObject(candidate) {
    return typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate);
}
function readDraftContext(storedContext) {
    if (!isNonArrayObject(storedContext))
        return undefined;
    if (storedContext.kind !== 'thread-followup' ||
        typeof storedContext.threadUuid !== 'string' ||
        !validateUuid(storedContext.threadUuid)) {
        return undefined;
    }
    return { kind: 'thread-followup', threadUuid: storedContext.threadUuid };
}
function readLexicalEditorState(storedEditorState) {
    if (!isNonArrayObject(storedEditorState) || !isNonArrayObject(storedEditorState.root)) {
        return undefined;
    }
    const { root } = storedEditorState;
    if (root.type !== 'root' || typeof root.version !== 'number' || !Array.isArray(root.children)) {
        return undefined;
    }
    // The repository validates Lexical's stable root envelope. Lexical validates registered node
    // payloads when the editor restores this serialized state.
    return storedEditorState;
}
export function readStoredAskInputDraft(storedDraft) {
    if (!isNonArrayObject(storedDraft))
        return undefined;
    const storedSnapshot = storedDraft.snapshot;
    if (!isNonArrayObject(storedSnapshot))
        return undefined;
    const uuid = storedDraft.uuid;
    const text = storedSnapshot.text;
    const hasValidIdentity = storedDraft.schemaVersion === 1 && typeof uuid === 'string' && validateUuid(uuid);
    const hasValidSnapshot = typeof text === 'string';
    if (!hasValidIdentity || !hasValidSnapshot)
        return undefined;
    const context = readDraftContext(storedDraft.context);
    const lexicalJson = readLexicalEditorState(storedSnapshot.lexicalJson);
    if (!context || !lexicalJson)
        return undefined;
    const { createdAtMs, updatedAtMs } = storedDraft;
    const hasValidTimestamps = typeof createdAtMs === 'number' &&
        Number.isFinite(createdAtMs) &&
        typeof updatedAtMs === 'number' &&
        Number.isFinite(updatedAtMs) &&
        createdAtMs >= 0 &&
        updatedAtMs >= createdAtMs;
    if (!hasValidTimestamps)
        return undefined;
    return {
        schemaVersion: 1,
        uuid,
        context,
        snapshot: { text, lexicalJson },
        createdAtMs,
        updatedAtMs,
    };
}
function isDraftWithinTtl(draft) {
    return draft.updatedAtMs >= Date.now() - DRAFT_TTL_MS;
}
function readValidDrafts(storedEntries) {
    return storedEntries
        .map(([storageKey, storedDraft]) => {
        const draft = readStoredAskInputDraft(storedDraft);
        if (!draft)
            return undefined;
        const isMatchingEntry = draft.uuid === storageKey;
        return isMatchingEntry && isDraftWithinTtl(draft) ? draft : undefined;
    })
        .filter((draft) => draft !== undefined)
        .sort((left, right) => right.updatedAtMs - left.updatedAtMs);
}
function readRetainedDrafts(storedEntries) {
    return readValidDrafts(storedEntries).slice(0, MAX_DRAFT_COUNT);
}
async function pruneDrafts(store) {
    const storedEntries = await entries(store);
    const retainedDraftKeys = new Set(readRetainedDrafts(storedEntries).map((draft) => draft.uuid));
    const draftKeysToDelete = storedEntries
        .filter(([storageKey]) => !retainedDraftKeys.has(storageKey))
        .map(([storageKey]) => storageKey);
    if (draftKeysToDelete.length > 0)
        await delMany(draftKeysToDelete, store);
}
export function createAskInputDraftRepository(accountId) {
    if (!accountId)
        return undefined;
    const store = getAccountScopedStore(ASK_INPUT_DRAFTS_DB_NAME, accountId, ASK_INPUT_DRAFTS_STORE_NAME);
    if (!store)
        return undefined;
    rememberAskInputDraftDatabase(accountId);
    return {
        getDraft: async (uuid) => {
            const draft = readStoredAskInputDraft(await get(uuid, store));
            if (!draft)
                return undefined;
            const isMatchingEntry = draft.uuid === uuid;
            return isMatchingEntry && isDraftWithinTtl(draft) ? draft : undefined;
        },
        listDrafts: async () => readRetainedDrafts(await entries(store)),
        upsertDraft: async (draft) => {
            await set(draft.uuid, draft, store);
            await pruneDrafts(store);
        },
        deleteDraft: (uuid) => del(uuid, store),
        clearDrafts: () => clear(store),
    };
}
//# sourceMappingURL=askInputDraftRepository.js.map